/**
 * AccountRequestsPanel — Admin review panel for resident/employee account requests.
 *
 * Shows pending invite-based account creation requests.
 * Admin can approve (creates Keycloak account) or reject.
 */

import React, { useState, useEffect, useCallback } from 'react';
import { X, CheckCircle, XCircle, Loader2, User, Mail, RefreshCw } from 'lucide-react';
import { cn } from '../../../utils/cn';
import { api } from '../../../services/api';
import toast from 'react-hot-toast';

interface AccountRequest {
  id: string;
  person_id: string;
  email?: string;
  status: string;
  created_at: string;
  person?: {
    first_name: string;
    last_name: string;
    email?: string;
  };
}

interface AccountRequestsPanelProps {
  open: boolean;
  customerId?: string;
  onClose: () => void;
}

export default function AccountRequestsPanel({
  open,
  customerId,
  onClose,
}: AccountRequestsPanelProps) {
  const [requests, setRequests] = useState<AccountRequest[]>([]);
  const [loading, setLoading] = useState(false);
  const [processingId, setProcessingId] = useState<string | null>(null);

  const load = useCallback(async () => {
    if (!customerId) return;
    setLoading(true);
    try {
      const resp = await api.get(`/customers/${customerId}/account-requests?status=pending`);
      setRequests(resp.data?.items || []);
    } catch {
      // silently fail — panel will show empty state
    } finally {
      setLoading(false);
    }
  }, [customerId]);

  useEffect(() => {
    if (open && customerId) load();
  }, [open, customerId, load]);

  const handleApprove = async (req: AccountRequest) => {
    setProcessingId(req.id);
    try {
      await api.post(`/customers/${customerId}/account-requests/${req.id}/approve`, {});
      toast.success('Conta criada! O usuário receberá um e-mail para definir a senha.');
      setRequests(prev => prev.filter(r => r.id !== req.id));
    } catch (err: any) {
      const msg = err.response?.data?.error || 'Erro ao aprovar';
      toast.error(msg);
    } finally {
      setProcessingId(null);
    }
  };

  const handleReject = async (req: AccountRequest) => {
    setProcessingId(req.id);
    try {
      await api.post(`/customers/${customerId}/account-requests/${req.id}/reject`, {});
      toast.success('Solicitação rejeitada.');
      setRequests(prev => prev.filter(r => r.id !== req.id));
    } catch (err: any) {
      const msg = err.response?.data?.error || 'Erro ao rejeitar';
      toast.error(msg);
    } finally {
      setProcessingId(null);
    }
  };

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-end pointer-events-none">
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black/40 pointer-events-auto"
        onClick={onClose}
      />

      {/* Panel */}
      <div className="relative w-full max-w-sm h-full bg-gray-950 border-l border-gray-800 flex flex-col pointer-events-auto shadow-2xl">
        {/* Header */}
        <div className="flex items-center justify-between px-4 py-3 border-b border-gray-800">
          <div className="flex items-center gap-2">
            <User className="h-4 w-4 text-blue-400" />
            <span className="text-white font-semibold text-sm">Solicitações de Conta</span>
            {requests.length > 0 && (
              <span className="bg-blue-500 text-white text-xs font-bold px-1.5 py-0.5 rounded-full">
                {requests.length}
              </span>
            )}
          </div>
          <div className="flex items-center gap-1">
            <button
              onClick={load}
              disabled={loading}
              className="p-1.5 text-gray-400 hover:text-white rounded transition-colors"
              title="Recarregar"
            >
              <RefreshCw className={cn('h-4 w-4', loading && 'animate-spin')} />
            </button>
            <button
              onClick={onClose}
              className="p-1.5 text-gray-400 hover:text-white rounded transition-colors"
            >
              <X className="h-4 w-4" />
            </button>
          </div>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto p-3 space-y-3">
          {loading && requests.length === 0 ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="h-6 w-6 animate-spin text-gray-500" />
            </div>
          ) : requests.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-12 text-center gap-2">
              <CheckCircle className="h-8 w-8 text-gray-700" />
              <p className="text-sm text-gray-500">Nenhuma solicitação pendente</p>
            </div>
          ) : (
            requests.map(req => {
              const isProcessing = processingId === req.id;
              const name = req.person
                ? `${req.person.first_name} ${req.person.last_name}`.trim()
                : 'Desconhecido';
              const email = req.email || req.person?.email;
              const date = new Date(req.created_at).toLocaleDateString('pt-BR');

              return (
                <div
                  key={req.id}
                  className="bg-gray-900 border border-gray-800 rounded-lg p-3 space-y-2"
                >
                  <div className="flex items-start gap-2">
                    <div className="w-8 h-8 rounded-full bg-blue-900/40 border border-blue-800/50 flex items-center justify-center flex-shrink-0">
                      <User className="h-4 w-4 text-blue-400" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-white text-sm font-medium truncate">{name}</p>
                      {email ? (
                        <div className="flex items-center gap-1 text-xs text-gray-400 mt-0.5">
                          <Mail className="h-3 w-3 flex-shrink-0" />
                          <span className="truncate">{email}</span>
                        </div>
                      ) : (
                        <p className="text-xs text-yellow-600 mt-0.5">Aguardando e-mail do usuário</p>
                      )}
                      <p className="text-xs text-gray-600 mt-0.5">{date}</p>
                    </div>
                  </div>

                  <div className="flex gap-2 pt-1">
                    <button
                      onClick={() => handleApprove(req)}
                      disabled={isProcessing || !email}
                      className={cn(
                        "flex-1 flex items-center justify-center gap-1.5 py-1.5 rounded text-xs font-semibold transition-colors",
                        email
                          ? "bg-green-600/20 hover:bg-green-600/30 text-green-400 border border-green-700/50"
                          : "bg-gray-800 text-gray-600 border border-gray-700 cursor-not-allowed"
                      )}
                      title={!email ? 'Aguardando e-mail do usuário' : undefined}
                    >
                      {isProcessing ? (
                        <Loader2 className="h-3.5 w-3.5 animate-spin" />
                      ) : (
                        <CheckCircle className="h-3.5 w-3.5" />
                      )}
                      Aprovar
                    </button>
                    <button
                      onClick={() => handleReject(req)}
                      disabled={isProcessing}
                      className="flex-1 flex items-center justify-center gap-1.5 py-1.5 rounded text-xs font-semibold bg-red-600/10 hover:bg-red-600/20 text-red-400 border border-red-700/30 transition-colors"
                    >
                      {isProcessing ? (
                        <Loader2 className="h-3.5 w-3.5 animate-spin" />
                      ) : (
                        <XCircle className="h-3.5 w-3.5" />
                      )}
                      Rejeitar
                    </button>
                  </div>
                </div>
              );
            })
          )}
        </div>
      </div>
    </div>
  );
}
